<script lang="ts" generics="TCommitReceipt = undefined">
  import { onDestroy, onMount, tick, untrack } from "svelte"
  import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import type { ContactOrderPostalSelection } from "../../../../../api/kehrblatt/contact-orders"
  import PostalRecipientEditor from "./_modules/postal-recipient-editor"
  import { postalRecipientPresentation } from "./_shared/_logic/postal-presentation"
  import { ContactOrderPlanningDraft } from "./_logic/planningDraft.svelte"
  import type { PostalRecipientFormValue } from "../postal-recipient-form"
  import TableStepLayout from "@kontextwerk/web-sdk/ui/layout/TableStepLayout.svelte"
  import type { TableRowsUpdater } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
  import type {
    ContactOrder,
    ContactOrderPlanningPreview,
  } from "../../../../../api/kehrblatt/contact-orders"
  import type { CostBenefitEstimate } from "../../../../../api/kehrblatt/cost-benefit"
  import { groupContactPointsByCustomerId } from "../../_logic/contact-quality-tags"
  import { scheduleEventHasContactOrder } from "../../../../../_shared/_logic/outreach-presentation"
  import { loadOrgBillingPlan, orgContactChannelCapabilities } from "../../_logic/org-billing-plan"
  import BatchFilterBar from "../batch-filter-bar"
  import { resolveBatchApplyTargets } from "../../_logic/batch-apply-targets"
  import { hasUsefulBatchSearch, hasUsefulBatchSelectOptions } from "../../_logic/filter-values"
  import { notifyPreconfigurationApplyResult } from "./_logic/preconfigurationApplyFeedback"
  import { loadAutomaticPreconfiguration } from "./_actions/loadAutomaticPreconfiguration"
  import {
    buildDigitalChannelTogglesForContactOrderRow,
    buildPostalToggleOptionsForContactOrderRow,
    postalSelectionFromValue,
    postalSelectionValue,
  } from "../../_logic/channels"
  import {
    type ContactOrderAppointmentRow,
    type ContactOrderDigitalChannel,
    type ContactOrderPlanningOperations,
    type ContactOrderPlanningRowPresentation,
    type ContactOrderPlanningSurfacePayloadBuilder,
    type ContactOrderPlanningSubmissionPhase,
    type ContactOrderPlanningSubmissionResult,
    type ContactOrderTableRow,
  } from "../../_logic/contact-orders-contract"
  import ContactOrderPlanningActions from "./_modules/contact-order-planning-actions"
  import { buildContactOrderCreateIntent } from "./build-create-contact-orders-intent"
  import {
    createContactOrderPlanningFilters,
    filterContactOrderPlanningRows,
    type ContactOrderPlanningFilterState,
  } from "./_logic/filters"
  import { scheduleEventHasCompletedCustomerInformation } from "./_shared/_logic/customer-information-state"
  import {
    contactOrderPlanningEventAllowsInteraction,
    contactOrderPlanningRowAllowsInteraction,
    pruneContactOrderPlanningSelectedIds,
  } from "./_logic/rowEligibility"
  import { buildContactOrderBatchPayloads } from "../../_logic/contact-orders-payload"
  import {
    applyContactOrderPreconfiguration,
    createContactOrderDigitalChannelChanges,
    type ContactOrderDigitalPreset,
    type ContactOrderPostalPreset,
  } from "./_shared/_logic/preconfiguration"
  import {
    createContactOrderPreviewController,
    type ContactOrderPreviewController,
  } from "./_logic/previewController"
  import {
    buildContactOrderAppointmentRows,
    buildContactOrderAppointmentsRefresh,
    filterPendingContactOrderRows,
  } from "./_logic/tableModel"
  import {
    mergeById,
    mergeRecipientContactPoints,
    workOrdersForRecipientScope,
  } from "./_logic/recipientQuickEditState"
  import {
    communicationSelectionForPlanningRow,
    hasActiveCommunicationForPlanningRow,
    reconcileSelectionStateForRows,
    resetPlanSelectionState,
    selectedDigitalChannelsForPlanningRow,
    selectedCustomerIdsForPlanningRow,
    setPostalSelectionState,
    toggleDigitalChannelSelectionState,
    type ContactOrderSelectionState,
  } from "../../_logic/selection-state"
  import {
    CONTACT_ORDER_APPOINTMENT_COLUMNS,
    buildContactOrderFilterFields,
    buildContactOrderLoadingRows,
    buildContactOrderMetricsVisible,
    buildContactOrderTableLoading,
    buildSkippedWarningText,
  } from "./_logic/tableState"
  import { contactOrderTargetEvents } from "../../_logic/create-result"
  import { runContactOrderPlanningSubmissionTransition } from "./_logic/submissionTransition"
  import {
    openRecipientQuickEdit,
    reloadRecipientQuickEditWorkOrders,
  } from "./recipient-quick-edit-commands"
  import type { QuickRecipientSaveResult } from "./_shared/_logic/recipient-quick-edit-contract"
  import CostBenefitNumbersBlock from "../cost-benefit-numbers-block"
  import ContactOrderPreconfigurationPanel from "./_modules/contact-order-preconfiguration-panel"
  import type { PreconfigurationChoice } from "../../_logic/preconfiguration-keep"
  import ContactOrderPlanningTable from "./_modules/contact-order-planning-table"

  type EmployeeOption = { label: string; value: string }

  type Props = {
    organizationId: string
    planningDraft?: ContactOrderPlanningDraft
    events: ScheduleEvent[]
    workOrders: WorkOrder[]
    properties: Property[]
    customers: Customer[]
    contactPoints: ContactPoint[]
    employeeOptions: EmployeeOption[]
    contactOrders?: ContactOrder[]
    caseIdByOrderId?: Record<string, string | null>
    initialFilterStatus?: ContactOrderPlanningFilterState["status"]
    rowPresentationByEventId?: Record<string, ContactOrderPlanningRowPresentation>
    initialRemovedEventIds?: string[]
    allowRemoveOnlySubmission?: boolean
    locked?: boolean
    readOnly?: boolean
    createButtonLabel?: string
    continueButtonLabel?: string
    backButtonLabel?: string
    showContinueButton?: boolean
    helpModalTextId?: string
    successMessage?: (createdCount: number) => string
    buildPayloads?: ContactOrderPlanningSurfacePayloadBuilder
    operations: ContactOrderPlanningOperations<TCommitReceipt>
    loading?: boolean
    onSubmissionResult: (
      result: ContactOrderPlanningSubmissionResult<TCommitReceipt>,
    ) => void | Promise<void>
    onRowsRemoved?: (eventIds: string[]) => void | Promise<void>
    onRowsRestored?: (eventIds: string[]) => void | Promise<void>
    onBack?: () => void
    setError?: (message: string | null) => void
  }

  const {
    organizationId,
    planningDraft = undefined,
    events,
    workOrders,
    properties,
    customers,
    contactPoints,
    employeeOptions,
    contactOrders = [],
    caseIdByOrderId = {},
    initialFilterStatus = "pending",
    rowPresentationByEventId = {},
    initialRemovedEventIds = [],
    allowRemoveOnlySubmission = false,
    locked = false,
    readOnly = false,
    createButtonLabel = "Ankündigungspläne anlegen",
    continueButtonLabel = "Überspringen",
    backButtonLabel = "Zurück",
    showContinueButton = true,
    helpModalTextId = "",
    successMessage = (createdCount) => `Ankündigungspläne angelegt: ${createdCount}.`,
    buildPayloads = buildContactOrderBatchPayloads,
    operations,
    loading = false,
    onSubmissionResult,
    onRowsRemoved = undefined,
    onRowsRestored = undefined,
    onBack = undefined,
    setError = () => {},
  }: Props = $props()

  const initialState = untrack(() => ({
    localEvents: events,
    localWorkOrders: workOrders,
    localCustomers: customers,
    localContactOrders: contactOrders,
    initialFilterStatus,
    initialRemovedEventIds,
  }))

  let localEvents = $state<ScheduleEvent[]>(initialState.localEvents)
  let localWorkOrders = $state<WorkOrder[]>(initialState.localWorkOrders)
  let localCustomers = $state<Customer[]>(initialState.localCustomers)
  let localContactPointUpdates = $state<ContactPoint[]>([])
  let deletedContactPointIds = $state<string[]>([])
  let localContactOrders = $state<ContactOrder[]>(initialState.localContactOrders)
  let submissionPhase = $state<ContactOrderPlanningSubmissionPhase>("idle")
  const draft = untrack(
    () =>
      planningDraft ??
      new ContactOrderPlanningDraft(organizationId, initialState.initialRemovedEventIds),
  )
  const selectionState = $derived(draft.selectionState)
  let dirtyScheduleEventIds = $state<Record<string, true>>({})
  let planningPreviewByEventId = $state<Record<string, ContactOrderPlanningPreview>>({})
  let planningPreviewErrorsByEventId = $state<Record<string, string>>({})
  let planningCostBenefit = $state<CostBenefitEstimate | null>(null)
  let planningInitialPreviewLoading = $state(false)
  let planningPreviewPendingEventIds = $state<string[]>([])
  let planningPreviewAttempted = $state(false)
  let contactFilters = $state<ContactOrderPlanningFilterState>(
    createContactOrderPlanningFilters({ status: initialState.initialFilterStatus }),
  )
  // A bulk edit changes only the fields the user expressly selects here.
  let digitalPreset = $state<PreconfigurationChoice<ContactOrderDigitalPreset>>("__keep__")
  let digitalChannelChanges = $state(createContactOrderDigitalChannelChanges())
  let postalPreset = $state<PreconfigurationChoice<ContactOrderPostalPreset>>("__keep__")
  let preconfigurationPending = $state(false)
  let selectedIds = $state<string[]>([])
  const removedEventIds = $derived(draft.removedEventIds)
  let planningPreviewController: ContactOrderPreviewController | null = null
  let updateTableRows: TableRowsUpdater | null = null
  let active = true
  // Kept for the lifetime of this draft, including an uncertain HTTP outcome.
  const contextActive = () => active && organizationId === draft.organizationId
  const buildCommitPayloads: ContactOrderPlanningSurfacePayloadBuilder = (input) =>
    buildPayloads({ ...input, confirmPreviewSelection: true }).map((payload) => {
      return {
        ...payload,
        planning_operation_id: draft.operationIdFor(
          payload.work_order_id,
          payload.schedule_event_id,
        ),
      }
    })

  const pendingContactOrders = $derived(
    localEvents.filter(
      (event) =>
        contactOrderPlanningEventAllowsInteraction(event.id, rowPresentationByEventId) &&
        !scheduleEventHasContactOrder(event, localContactOrders) &&
        !scheduleEventHasCompletedCustomerInformation(event) &&
        Boolean(event.work_order_id),
    ),
  )
  const pendingCount = $derived(pendingContactOrders.length)
  const pendingEventIds = $derived(new Set(pendingContactOrders.map((event) => event.id)))
  const effectiveRemovedEventIds = $derived(
    removedEventIds.filter((eventId) => pendingEventIds.has(eventId)),
  )
  const totalCount = $derived(localEvents.length)
  const contactStepLocked = $derived(
    Boolean(readOnly || locked) ||
      organizationId !== draft.organizationId ||
      (totalCount > 0 && pendingCount === 0),
  )
  const submissionPending = $derived(submissionPhase !== "idle" || preconfigurationPending)
  const tableLoading = $derived(
    buildContactOrderTableLoading({
      loading,
      previewLoading: planningInitialPreviewLoading,
    }),
  )
  const allowedContactChannels = $derived($orgContactChannelCapabilities(organizationId))
  const employeeLabelById = $derived(
    Object.fromEntries(employeeOptions.map((employee) => [employee.value, employee.label])),
  )
  const workOrderById = $derived(
    Object.fromEntries(localWorkOrders.map((order) => [order.id, order])),
  )
  const propertyById = $derived(
    Object.fromEntries(properties.map((property) => [property.id, property])),
  )
  const customersById = $derived(
    Object.fromEntries(localCustomers.map((customer) => [customer.id, customer])),
  )
  const localContactPoints = $derived(
    mergeRecipientContactPoints(contactPoints, localContactPointUpdates, deletedContactPointIds),
  )
  const contactPointsByCustomerId = $derived(groupContactPointsByCustomerId(localContactPoints))
  const appointmentRows: ContactOrderAppointmentRow[] = $derived(
    buildContactOrderAppointmentRows({
      plannedAppointments: localEvents,
      createdOrderById: workOrderById,
      propertyById,
      employeeLabelById,
      tagContext: { propertyById, customersById, contactPointsByCustomerId },
      planningPreviewByEventId,
      planningPreviewErrorsByEventId,
      dirtyScheduleEventIds,
    }),
  )
  const isEventRemoved = (eventId: string) => removedEventIds.includes(eventId)
  const isRowRemoved = (row: ContactOrderAppointmentRow) => isEventRemoved(row.event.id)
  const rowIsInteractionDisabled = (row: ContactOrderAppointmentRow) =>
    !contactOrderPlanningRowAllowsInteraction(row, rowPresentationByEventId)
  const isConfirmationInformationRow = (row: ContactOrderAppointmentRow) =>
    String(row.customerInformationMode ?? "").startsWith("confirmation")
  const displayAppointmentRows = $derived(
    appointmentRows.map((row) => ({
      ...row,
      removed: isRowRemoved(row),
    })),
  )
  const loadingRows = $derived<ContactOrderTableRow[]>(
    buildContactOrderLoadingRows(tableLoading, totalCount),
  )
  const selectedCustomerIdsForRow = (row: ContactOrderAppointmentRow) =>
    selectedCustomerIdsForPlanningRow(row, selectionState, allowedContactChannels)
  const selectedDigitalChannelsForRow = (row: ContactOrderAppointmentRow) =>
    isRowRemoved(row) || isConfirmationInformationRow(row)
      ? []
      : selectedDigitalChannelsForPlanningRow(row, selectionState, allowedContactChannels)
  const communicationSelectionForRow = (row: ContactOrderAppointmentRow) =>
    isRowRemoved(row) || isConfirmationInformationRow(row)
      ? { digitalByCustomerId: {}, postal: { mode: "DISABLED" as const } }
      : communicationSelectionForPlanningRow(row, selectionState, allowedContactChannels)
  const visibleAppointmentRows = $derived(
    filterContactOrderPlanningRows(
      displayAppointmentRows,
      contactFilters,
      selectedCustomerIdsForRow,
      selectedDigitalChannelsForRow,
    ),
  )
  const actionableSelectedIds = $derived(
    pruneContactOrderPlanningSelectedIds({
      selectedIds,
      visibleRows: visibleAppointmentRows,
      presentationByEventId: rowPresentationByEventId,
    }),
  )
  const tableRows = $derived<ContactOrderTableRow[]>(
    tableLoading ? loadingRows : visibleAppointmentRows,
  )
  const digitalChannelTogglesForRow = (row: ContactOrderAppointmentRow) =>
    buildDigitalChannelTogglesForContactOrderRow({
      row,
      digitalPlan: communicationSelectionForRow(row).digitalByCustomerId,
      allowedChannels: allowedContactChannels,
      pending: isAnnouncementPlanPending(row),
    })
  const postalOptionsForRow = (row: ContactOrderAppointmentRow) =>
    buildPostalToggleOptionsForContactOrderRow(row, allowedContactChannels)

  const configuredPostalSelectionForRow = (
    row: ContactOrderAppointmentRow,
  ): ContactOrderPostalSelection => {
    const active = communicationSelectionForRow(row).postal

    if (active.mode !== "DISABLED") return active

    const saved = draft.inactivePostalSelections[String(row.id)]

    if (saved) return saved

    const options = postalOptionsForRow(row)
    const option =
      options.find((option) => option.mode === "PERSONAL" && !option.disabled) ??
      options.find((option) => option.mode !== "DISABLED" && !option.disabled)
    const selection = option ? postalSelectionFromValue(option.value, [option]) : null

    return selection
      ? {
          ...selection,
          dispatch_mode:
            selection.mode === "PERSONAL" && row.planningPreview?.automatic_postal_available
              ? (row.planningPreview.postal_dispatch_default ?? "MANUAL")
              : "MANUAL",
        }
      : { mode: "DISABLED" }
  }
  const pendingRows = $derived(
    filterPendingContactOrderRows(appointmentRows).filter((row) => !rowIsInteractionDisabled(row)),
  )
  const pendingRowIds = $derived(new Set(pendingRows.map((row) => String(row.id))))
  const applyTargetResolution = $derived(
    resolveBatchApplyTargets({
      filteredRows: visibleAppointmentRows.filter(
        (row) =>
          pendingRowIds.has(String(row.id)) && !row.removed && !isConfirmationInformationRow(row),
      ),
      selectedIds: actionableSelectedIds,
      getId: (row) => String(row.id),
    }),
  )
  const activePlanPendingRows = $derived(
    pendingRows.filter(
      (row) =>
        !isRowRemoved(row) &&
        (isConfirmationInformationRow(row) ||
          hasActiveCommunicationForPlanningRow(row, selectionState, allowedContactChannels)),
    ),
  )
  const skippedPendingCount = $derived(pendingRows.length - activePlanPendingRows.length)
  const pendingContactableCount = $derived(activePlanPendingRows.length)
  const skippedWarningText = $derived(buildSkippedWarningText(skippedPendingCount))
  const createIntent = $derived(
    buildContactOrderCreateIntent({
      loading: tableLoading || preconfigurationPending,
      locked: contactStepLocked,
      pendingContactableCount,
      pendingContactOrders,
      appointmentRows,
      selectionState,
      allowedContactChannels,
      removedEventIds: effectiveRemovedEventIds,
      allowRemoveOnlySubmission,
      buildPayloads: buildCommitPayloads,
      caseIdByOrderId,
      events: localEvents,
      contactOrders: localContactOrders,
      workOrders: localWorkOrders,
      label: createButtonLabel,
      successMessage,
    }),
  )
  const filterFields = $derived(
    buildContactOrderFilterFields({
      filters: contactFilters,
      rowCount: appointmentRows.length,
      hasUsefulSearch: hasUsefulBatchSearch,
      hasUsefulSelectOptions: hasUsefulBatchSelectOptions,
    }),
  )
  const showMetrics = $derived(
    buildContactOrderMetricsVisible({
      hasEstimate: Boolean(planningCostBenefit),
      pendingCount,
      skippedPendingCount,
      loading: loading || planningInitialPreviewLoading,
    }),
  )
  const costBenefitLoading = $derived(loading || planningInitialPreviewLoading)
  const costBenefitError = $derived(
    planningPreviewAttempted &&
      !costBenefitLoading &&
      pendingCount > 0 &&
      planningCostBenefit === null
      ? "Kosten-Nutzen-Schätzung konnte nicht berechnet werden. Bitte prüfen Sie die Preview-Antwort."
      : null,
  )
  const appointmentsRefresh = $derived(
    [
      buildContactOrderAppointmentsRefresh(appointmentRows),
      allowedContactChannels.join(","),
      Object.entries(rowPresentationByEventId)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(
          ([eventId, presentation]) =>
            `${eventId}:${presentation.rowNumber ?? ""}:${presentation.disabled ? "disabled" : "enabled"}:${presentation.selectable === false ? "fixed" : "selectable"}:${presentation.muted ? "muted" : "plain"}`,
        )
        .join("|"),
    ].join("::"),
  )
  const isAnnouncementPlanPending = (row: ContactOrderAppointmentRow) =>
    planningPreviewPendingEventIds.includes(row.event.id)

  const setSelectionState = (next: ContactOrderSelectionState) => {
    if (contextActive()) draft.selectionState = next
  }

  const syncAppointmentTableRows = () => {
    const nextRows = tableRows
    const nextSelectedIds = pruneContactOrderPlanningSelectedIds({
      selectedIds,
      visibleRows: visibleAppointmentRows,
      presentationByEventId: rowPresentationByEventId,
    })

    if (nextSelectedIds.length !== selectedIds.length) selectedIds = nextSelectedIds

    updateTableRows?.(() => nextRows, { total: nextRows.length })
  }
  const requestAppointmentTableRowsSync = () => {
    queueMicrotask(syncAppointmentTableRows)
  }

  const buildContactOrderPayloadsForEvents = (
    targetEvents: ScheduleEvent[],
    includeInactiveRows = false,
  ) =>
    buildPayloads({
      targetEvents,
      appointmentRows,
      caseIdByOrderId,
      communicationSelectionByEventId: selectionState.communicationSelectionByEventId,
      allowedContactChannels,
      includeInactiveRows,
    })

  planningPreviewController = createContactOrderPreviewController({
    getTargetEvents: () => {
      if (loading) return []

      return contactOrderTargetEvents({
        pendingContactOrders,
        appointmentRows,
        selectionState,
        allowedContactChannels,
        includeAutomaticRows: true,
      }).filter((event) => !isEventRemoved(event.id))
    },
    buildPayloads: (targetEvents) => buildContactOrderPayloadsForEvents(targetEvents, true),
    loadPreview: (payloads) => operations.previewContactOrders(payloads),
    setPreviewByEventId: (next) => {
      if (!contextActive()) return

      planningPreviewByEventId = next
      queueMicrotask(() => {
        if (!contextActive()) return

        const selections = { ...selectionState.communicationSelectionByEventId }
        for (const row of appointmentRows) {
          if (row.planningPreview && !selections[row.event.id]) {
            selections[row.event.id] = communicationSelectionForPlanningRow(
              row,
              selectionState,
              allowedContactChannels,
            )
          }
        }
        draft.selectionState = { communicationSelectionByEventId: selections }
        setSelectionState(
          reconcileSelectionStateForRows(appointmentRows, selectionState, allowedContactChannels),
        )
        syncAppointmentTableRows()
      })
    },
    setErrorsByEventId: (next) => {
      if (!contextActive()) return

      planningPreviewErrorsByEventId = next
      requestAppointmentTableRowsSync()
    },
    setCostBenefit: (next) => {
      if (!contextActive()) return

      planningCostBenefit = next
    },
    setInitialLoading: (next) => {
      if (!contextActive()) return

      if (next) planningPreviewAttempted = true

      planningInitialPreviewLoading = next
    },
    setPendingEventIds: (next) => {
      if (!contextActive()) return

      planningPreviewPendingEventIds = next
      requestAppointmentTableRowsSync()
    },
  })

  const schedulePlanningPreviewForRows = (rows: ContactOrderAppointmentRow[], delay = 220) => {
    planningPreviewController?.queueForEvents(
      rows
        .map((row) => row.event)
        .filter(
          (event) =>
            event.work_order_id &&
            !event.contact_order_id &&
            !isEventRemoved(event.id) &&
            contactOrderPlanningEventAllowsInteraction(event.id, rowPresentationByEventId),
        ),
      delay,
    )
  }

  const schedulePlanningPreviewForWorkOrderIds = (workOrderIds: string[], delay = 220) => {
    const targetWorkOrderIds = new Set(workOrderIds)
    planningPreviewController?.queueForEvents(
      pendingContactOrders.filter(
        (event) =>
          event.work_order_id &&
          targetWorkOrderIds.has(event.work_order_id) &&
          !isEventRemoved(event.id),
      ),
      delay,
    )
  }

  const handleRecipientQuickEditSaved = async (
    workOrder: WorkOrder,
    result: QuickRecipientSaveResult,
  ) => {
    if (!contextActive()) return

    const changedCustomerIds = [
      ...result.customers.map((customer) => customer.id),
      ...result.contactPoints.flatMap((point) => (point.customer_id ? [point.customer_id] : [])),
      ...localContactPoints.flatMap((point) =>
        result.deletedContactPointIds.includes(point.id) && point.customer_id
          ? [point.customer_id]
          : [],
      ),
    ]
    const affectedWorkOrders = workOrdersForRecipientScope(
      localWorkOrders,
      workOrder,
      changedCustomerIds,
    )
    const affectedWorkOrderIds = affectedWorkOrders.map((entry) => entry.id)

    if (result.customers.length) localCustomers = mergeById(localCustomers, result.customers)

    if (result.contactPoints.length)
      localContactPointUpdates = mergeById(localContactPointUpdates, result.contactPoints)

    deletedContactPointIds = [
      ...new Set([...deletedContactPointIds, ...result.deletedContactPointIds]),
    ]
    planningPreviewController?.invalidateForEvents(
      localEvents.filter(
        (event) => event.work_order_id && affectedWorkOrderIds.includes(event.work_order_id),
      ),
    )
    const reloaded = await reloadRecipientQuickEditWorkOrders({
      organizationId,
      workOrderIds: affectedWorkOrderIds,
    })

    if (!contextActive()) return

    if (reloaded.length) localWorkOrders = mergeById(localWorkOrders, reloaded)

    await tick()

    if (!contextActive()) return

    setSelectionState(
      reconcileSelectionStateForRows(appointmentRows, selectionState, allowedContactChannels),
    )
    requestAppointmentTableRowsSync()
    schedulePlanningPreviewForWorkOrderIds(affectedWorkOrderIds, 0)
  }

  const openRecipientQuickEditForRow = (row: ContactOrderAppointmentRow, event?: MouseEvent) => {
    event?.stopPropagation()

    if (
      tableLoading ||
      contactStepLocked ||
      submissionPending ||
      rowIsInteractionDisabled(row) ||
      isRowRemoved(row) ||
      isConfirmationInformationRow(row)
    )
      return

    const workOrder = row.event.work_order_id
      ? (workOrderById[row.event.work_order_id] ?? null)
      : null
    openRecipientQuickEdit({
      organizationId,
      row,
      workOrder,
      customers: localCustomers,
      contactPoints: localContactPoints,
      onSaved: (result: QuickRecipientSaveResult) =>
        handleRecipientQuickEditSaved(workOrder!, result),
    })
  }

  onMount(() => {
    void loadOrgBillingPlan(organizationId)
    planningPreviewController?.mount()

    return () => planningPreviewController?.unmount()
  })
  onDestroy(() => {
    active = false
    planningPreviewController?.destroy()
  })

  const applyPreconfigurationToRows = async () => {
    if (tableLoading || contactStepLocked || submissionPending) return

    const targetRows = applyTargetResolution.targetRows
    const capturedState = selectionState
    const selectedDigitalPreset = digitalPreset
    const selectedPostalPreset = postalPreset
    let automaticSelectionsByEventId

    if (selectedDigitalPreset === "auto") {
      preconfigurationPending = true
      try {
        automaticSelectionsByEventId = await loadAutomaticPreconfiguration({
          rows: targetRows,
          buildPayloads,
          previewContactOrders: operations.previewContactOrders,
          caseIdByOrderId,
          allowedContactChannels,
        })
      } catch {
        if (contextActive())
          notifyPreconfigurationApplyResult({
            total: targetRows.length,
            applied: 0,
            reason:
              "Die automatische Kontaktwegeauswahl konnte nicht geladen werden. Bitte erneut anwenden.",
          })

        return
      } finally {
        preconfigurationPending = false
      }

      if (!contextActive() || contactStepLocked) return

      const currentEvents = new Set(
        pendingRows
          .filter((row) => !isRowRemoved(row) && !isConfirmationInformationRow(row))
          .map((row) => row.event),
      )

      if (
        selectionState !== capturedState ||
        targetRows.some((row) => !currentEvents.has(row.event))
      ) {
        notifyPreconfigurationApplyResult({
          total: targetRows.length,
          applied: 0,
          reason: "Die Zeilen wurden inzwischen aktualisiert. Bitte erneut anwenden.",
        })

        return
      }
    }

    const result = applyContactOrderPreconfiguration({
      targetRows,
      digitalPreset: selectedDigitalPreset,
      digitalChannelChanges,
      postalPreset: selectedPostalPreset,
      automaticSelectionsByEventId,
      state: selectionState,
      allowedContactChannels,
    })
    for (const row of result.appliedRows) {
      const previous = communicationSelectionForRow(row).postal
      const next = result.state.communicationSelectionByEventId[String(row.id)]?.postal

      if (next?.mode === "DISABLED" && previous.mode !== "DISABLED") {
        draft.inactivePostalSelections[String(row.id)] = previous
      } else if (next && next.mode !== "DISABLED") {
        delete draft.inactivePostalSelections[String(row.id)]
      }
    }

    setSelectionState(result.state)
    notifyPreconfigurationApplyResult({
      total: targetRows.length,
      applied: result.applied,
      skipped: result.skipped,
      reasonCounts: result.skippedReasons,
      reason:
        "Passende Ansprechpartner, Kontaktwege oder die gewählte Zustellart sind in diesen Zeilen nicht verfügbar. Diese Zeilen wurden nicht verändert.",
    })
    requestAppointmentTableRowsSync()
    schedulePlanningPreviewForRows(result.appliedRows)
  }

  const updateFilter = (fieldId: string, value: unknown) => {
    contactFilters = {
      ...contactFilters,
      [fieldId]: String(value ?? ""),
    }
    requestAppointmentTableRowsSync()
  }

  const toggleDigitalChannel = (
    row: ContactOrderAppointmentRow,
    customerId: string,
    channel: ContactOrderDigitalChannel,
    event?: MouseEvent,
  ) => {
    event?.stopPropagation()

    if (isConfirmationInformationRow(row)) return

    if (tableLoading || contactStepLocked || submissionPending || rowIsInteractionDisabled(row))
      return

    setError(null)
    const nextSelectionState = toggleDigitalChannelSelectionState({
      row,
      customerId,
      channel,
      state: selectionState,
      allowedContactChannels,
    })

    if (nextSelectionState === selectionState) return

    setSelectionState(nextSelectionState)
    requestAppointmentTableRowsSync()
    schedulePlanningPreviewForRows([row])
  }

  const selectPostal = (
    row: ContactOrderAppointmentRow,
    value: string,
    dispatch: "MANUAL" | "AUTOMATIC",
  ) => {
    if (isConfirmationInformationRow(row)) return

    if (tableLoading || contactStepLocked || submissionPending || rowIsInteractionDisabled(row))
      return

    if (dispatch === "AUTOMATIC" && !row.planningPreview?.automatic_postal_available) return

    if (value === "INDIVIDUAL") {
      const existing = configuredPostalSelectionForRow(row)

      if (existing.manual_recipient) {
        updatePostalDraft(row, { ...existing, dispatch_mode: dispatch })
      } else {
        openPostalEditor(row, true, dispatch)
      }

      return
    }

    const current = configuredPostalSelectionForRow(row)
    const postal =
      postalSelectionValue(current) === value
        ? current
        : postalSelectionFromValue(value, postalOptionsForRow(row))

    if (!postal) return

    updatePostalDraft(row, {
      ...postal,
      dispatch_mode: postal.mode === "PERSONAL" ? dispatch : "MANUAL",
    })
  }

  const updatePostalDraft = (
    row: ContactOrderAppointmentRow,
    postal: ContactOrderPostalSelection,
    enabled = communicationSelectionForRow(row).postal.mode !== "DISABLED",
  ) => {
    if (
      !active ||
      tableLoading ||
      contactStepLocked ||
      submissionPending ||
      rowIsInteractionDisabled(row) ||
      isRowRemoved(row)
    )
      return

    const rowKey = String(row.id)

    if (!enabled) {
      draft.inactivePostalSelections[rowKey] = postal

      if (communicationSelectionForRow(row).postal.mode === "DISABLED") {
        requestAppointmentTableRowsSync()

        return
      }

      postal = { mode: "DISABLED" }
    } else {
      delete draft.inactivePostalSelections[rowKey]
    }

    setError(null)
    setSelectionState(
      setPostalSelectionState({ row, postal, state: selectionState, allowedContactChannels }),
    )
    requestAppointmentTableRowsSync()
    schedulePlanningPreviewForRows([row])
  }

  const togglePostal = (row: ContactOrderAppointmentRow) => {
    const enabled = communicationSelectionForRow(row).postal.mode === "DISABLED"
    const postal = configuredPostalSelectionForRow(row)

    if (
      isConfirmationInformationRow(row) ||
      postal.mode === "DISABLED" ||
      (enabled &&
        postal.dispatch_mode === "AUTOMATIC" &&
        !row.planningPreview?.automatic_postal_available)
    )
      return

    updatePostalDraft(row, postal, enabled)
  }

  const openPostalEditor = (
    row: ContactOrderAppointmentRow,
    individual = false,
    dispatch?: "MANUAL" | "AUTOMATIC",
  ) => {
    if (
      !active ||
      tableLoading ||
      contactStepLocked ||
      submissionPending ||
      rowIsInteractionDisabled(row) ||
      isRowRemoved(row)
    )
      return

    const original = configuredPostalSelectionForRow(row)

    if (!individual && original.mode !== "PERSONAL") return

    const organization = organizationId
    const originalKey = JSON.stringify(original)
    const originalActive = communicationSelectionForRow(row).postal.mode !== "DISABLED"
    const recipient = postalRecipientPresentation(original, row.planningPreview)
    const order = workOrderById[row.event.work_order_id ?? ""]
    const property = propertyById[order?.property_id ?? ""]
    const address = recipient.address ?? property
    const manual = individual || Boolean(original.manual_recipient)
    openOverlay({
      title: "Briefempfänger bearbeiten",
      content: PostalRecipientEditor,
      closeOnParentOpen: false,
      properties: {
        manual,
        initialValue: {
          recipientName: individual && !original.manual_recipient ? "" : recipient.name,
          country: address?.country ?? "Deutschland",
          street: address?.street ?? "",
          house_number: address?.house_number ?? "",
          zip: address?.zip ?? "",
          city: address?.city ?? "",
          address_extra: address?.address_extra ?? "",
        },
        onApply: (value: PostalRecipientFormValue) => {
          const currentRow = appointmentRows.find((item) => item.id === row.id)

          if (
            !active ||
            organization !== organizationId ||
            !currentRow ||
            submissionPending ||
            contactStepLocked ||
            tableLoading ||
            rowIsInteractionDisabled(currentRow) ||
            isRowRemoved(currentRow) ||
            JSON.stringify(configuredPostalSelectionForRow(currentRow)) !== originalKey ||
            (communicationSelectionForRow(currentRow).postal.mode !== "DISABLED") !== originalActive
          )
            return "Die Auswahl wurde inzwischen geändert. Bitte die Bearbeitung erneut öffnen."

          const postal: ContactOrderPostalSelection = manual
            ? {
                mode: "PERSONAL",
                manual_recipient: {
                  name: value.recipientName.trim(),
                  address: {
                    country: value.country.trim(),
                    street: value.street.trim(),
                    house_number: value.house_number.trim(),
                    zip: value.zip.trim(),
                    city: value.city.trim(),
                    address_extra: value.address_extra?.trim() || null,
                  },
                },
                dispatch_mode:
                  dispatch ??
                  (original.mode === "PERSONAL"
                    ? (original.dispatch_mode ?? "MANUAL")
                    : currentRow.planningPreview?.automatic_postal_available
                      ? (currentRow.planningPreview.postal_dispatch_default ?? "MANUAL")
                      : "MANUAL"),
              }
            : { ...original, recipient_name: value.recipientName.trim() }
          updatePostalDraft(currentRow, postal)

          return null
        },
      },
    })
  }

  const disablePlansForRows = (rows: ContactOrderAppointmentRow[]) => {
    if (tableLoading || contactStepLocked || submissionPending) return

    const removableRows = rows.filter((row) => !isRowRemoved(row) && !rowIsInteractionDisabled(row))

    if (!removableRows.length) return

    setError(null)
    draft.removedEventIds = Array.from(
      new Set([...removedEventIds, ...removableRows.map((row) => row.event.id)]),
    )
    const removedRowIds = new Set(removableRows.map((row) => String(row.id)))
    selectedIds = selectedIds.filter((id) => !removedRowIds.has(id))
    requestAppointmentTableRowsSync()
    planningPreviewController?.recomputeCostBenefit()
  }

  const disablePlanForRow = (row: ContactOrderAppointmentRow, event?: MouseEvent) => {
    event?.stopPropagation()
    disablePlansForRows([row])
  }

  const enablePlanForRow = async (row: ContactOrderAppointmentRow, event?: MouseEvent) => {
    event?.stopPropagation()

    if (tableLoading || contactStepLocked || submissionPending || rowIsInteractionDisabled(row))
      return

    if (!isRowRemoved(row)) return

    setError(null)
    await onRowsRestored?.([row.event.id])
    draft.removedEventIds = removedEventIds.filter((id) => id !== row.event.id)
    requestAppointmentTableRowsSync()
    planningPreviewController?.recomputeCostBenefit()
    schedulePlanningPreviewForRows([row])
  }

  const resetPlansForRows = (rows: ContactOrderAppointmentRow[]) => {
    if (tableLoading || contactStepLocked || submissionPending) return

    const resettableRows = rows.filter(
      (row) =>
        !rowIsInteractionDisabled(row) && !isRowRemoved(row) && !isConfirmationInformationRow(row),
    )

    if (!resettableRows.length) return

    setError(null)
    let nextSelectionState = selectionState
    for (const row of resettableRows) {
      delete draft.inactivePostalSelections[String(row.id)]
      nextSelectionState = resetPlanSelectionState(row, nextSelectionState)
    }
    setSelectionState(nextSelectionState)
    requestAppointmentTableRowsSync()
    schedulePlanningPreviewForRows(resettableRows)
  }

  const buildContinueResult = (dirtyEventsPersisted: boolean) => ({
    createdContactOrders: localContactOrders,
    scheduleEvents: localEvents,
    dirtyEventsPersisted,
  })

  const handleSubmissionResult = (result: ContactOrderPlanningSubmissionResult<TCommitReceipt>) =>
    runContactOrderPlanningSubmissionTransition({
      result,
      onRowsRemoved,
      onCommitted: (committed) => {
        if (!contextActive()) return

        localEvents = committed.result.scheduleEvents
        localContactOrders = committed.result.createdContactOrders
        setError(
          committed.status === "partial"
            ? "Einige Ankündigungspläne konnten nicht angelegt werden."
            : committed.status === "empty"
              ? "Keine Kundeninformation abgeschlossen."
              : null,
        )
      },
      onSubmissionResult,
    })
</script>

<div class="wrapper">
  <ContactOrderPlanningActions
    loading={tableLoading || preconfigurationPending}
    phase={submissionPhase}
    {createIntent}
    continuationResult={buildContinueResult(false)}
    {operations}
    {showContinueButton}
    {backButtonLabel}
    {continueButtonLabel}
    {onBack}
    onPhaseChange={(phase) => (submissionPhase = phase)}
    onSubmissionResult={handleSubmissionResult}
  >
    {#snippet children(primaryAction, footerActions)}
      <TableStepLayout
        titleActions={readOnly ? null : primaryAction}
        title="Terminankündigung"
        count={tableLoading
          ? appointmentRows.length
          : visibleAppointmentRows.filter((row) => !row.removed).length}
        darkMode={true}
        showPreconfiguration={!contactStepLocked}
        {showMetrics}
        showFooter={!readOnly}
        {helpModalTextId}
      >
        {#snippet preconfiguration()}
          <ContactOrderPreconfigurationPanel
            selectedCount={actionableSelectedIds.length}
            targetCount={applyTargetResolution.targetRows.length}
            pending={submissionPending}
            disabled={tableLoading || contactStepLocked || submissionPending}
            {digitalPreset}
            {digitalChannelChanges}
            {postalPreset}
            onApply={applyPreconfigurationToRows}
            onDigitalPresetChange={(value) => (digitalPreset = value)}
            onDigitalChannelChange={(channel, value) => {
              digitalChannelChanges = { ...digitalChannelChanges, [channel]: value }
            }}
            onPostalPresetChange={(value) => (postalPreset = value)}
          />
        {/snippet}

        {#snippet filters()}
          <BatchFilterBar
            fields={filterFields}
            disabled={tableLoading || submissionPending || readOnly}
            onChange={updateFilter}
          />
        {/snippet}

        {#snippet metrics()}
          <CostBenefitNumbersBlock
            estimate={planningCostBenefit}
            loading={costBenefitLoading}
            error={costBenefitError}
            darkMode={true}
          />
          {#if skippedPendingCount}
            <p class="contact-warning">{skippedWarningText}</p>
          {/if}
        {/snippet}

        {#snippet footer()}
          {@render footerActions()}
        {/snippet}

        <ContactOrderPlanningTable
          columns={CONTACT_ORDER_APPOINTMENT_COLUMNS}
          rows={tableRows}
          loading={tableLoading}
          {readOnly}
          refreshKey={tableLoading
            ? `contact-order-loading:${tableRows.length}`
            : appointmentsRefresh}
          disabled={tableLoading || contactStepLocked || submissionPending}
          getDigitalChannelToggles={digitalChannelTogglesForRow}
          getCommunicationSelection={communicationSelectionForRow}
          getConfiguredPostalSelection={configuredPostalSelectionForRow}
          getPostalOptions={postalOptionsForRow}
          {isAnnouncementPlanPending}
          selectedIds={actionableSelectedIds}
          {rowPresentationByEventId}
          onRowsUpdaterReady={(updater) => {
            updateTableRows = updater
            requestAppointmentTableRowsSync()
          }}
          onSelectionChange={(ids) => {
            selectedIds = pruneContactOrderPlanningSelectedIds({
              selectedIds: ids,
              visibleRows: visibleAppointmentRows,
              presentationByEventId: rowPresentationByEventId,
            })
          }}
          onToggleDigitalChannel={toggleDigitalChannel}
          onSelectPostal={selectPostal}
          onTogglePostal={togglePostal}
          onEditPostal={(row) => openPostalEditor(row)}
          onDisablePlan={disablePlanForRow}
          onEnablePlan={enablePlanForRow}
          onRemoveRows={disablePlansForRows}
          onResetRows={resetPlansForRows}
          onEditRecipients={openRecipientQuickEditForRow}
        />
      </TableStepLayout>
    {/snippet}
  </ContactOrderPlanningActions>
</div>

<style lang="less">
  .wrapper {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    width: 100%;
    min-width: 0;
  }

  .contact-warning {
    margin: -0.45rem 0 0;
    color: color-mix(in srgb, var(--text-invers-100) 78%, transparent);
    font-size: 0.88rem;
    line-height: 1.35;
  }
</style>
